import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { useNavigate } from '@remix-run/react';
import type { TButtonElement } from '@shared/dream-components';
import { ButtonElement } from '@shared/dream-components';
import { produce } from 'immer';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { usePageContext } from '~/context/usePageContext';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { Product } from '~/interfaces/product';
import type { StaticPageLoaderData } from '~/services/static_page';

import { useModalContext } from '../context/useModalContext';

import useProductMergeTagProcessor from '../hooks/useProductMergeTagProcessor';

import BuyNowAction from './BuyNowAction';

interface Props {
  node: TButtonElement;
  children: ReactNode | ReactNode[];
}

const Button = ({ node, children }: Props) => {
  const navigate = useNavigate();
  const { handleOpenModal, handleCloseModal } = useGlobalModals();
  const { subscription, setCloseModalActionDispatched, openPageModalType } = usePageContext();
  const { product } = useTypedLoaderData<StaticPageLoaderData & { product?: Product }>();
  const modalContext = useModalContext();

  const { children: productButtonChildren } = useProductMergeTagProcessor({ node, children });

  const mergeTag = node?.attrs?.mergeTag;
  const onClick = node?.attrs && 'onClick' in node.attrs ? node.attrs.onClick : undefined;
  const action = node?.attrs && 'action' in node.attrs && node.attrs.action;
  const currentHref = node.attrs?.url || '';
  const isOpenAction = onClick === 'action' || (onClick === undefined && !!action);
  let url: URL | undefined;
  let handleClientSideNavigation: any;
  let isClientSideNavigation = false;

  const isTelLink = currentHref.startsWith('tel:');

  try {
    if (!isTelLink) {
      url = new URL(currentHref);
    }
  } catch {
    // Fail silently
  }

  // On actions we only want to
  if (isOpenAction) {
    if (typeof window !== 'undefined') {
      url = new URL(window.location.href);
    }

    if (action === 'login' && url) {
      url.searchParams.set('modal', 'login');
    } else if (action === 'sign_up' && url) {
      url.searchParams.set('modal', 'signup');
    } else if (action === 'profile' && url) {
      url.pathname = `subscribe/${subscription?.subscriber_id || ''}/manage`;
    } else if (action === 'search' && url) {
      url.searchParams.set('modal', 'search');
    } else if (action === 'close_modal' && url) {
      url.searchParams.delete('modal');
    }
  }

  // Ensure the button is not open in a new tab if it has an action
  const nodeWithModifiedAttrs = useMemo(
    () =>
      produce(node, (draft) => {
        if (draft.attrs && isOpenAction && url) {
          draft.attrs.open_in_new_tab = false;
        }
      }),
    [node, isOpenAction, url],
  );

  if (
    !nodeWithModifiedAttrs.attrs?.open_in_new_tab &&
    url &&
    !nodeWithModifiedAttrs.attrs?.isCustomLink &&
    !isTelLink
  ) {
    handleClientSideNavigation = () => {
      if (typeof action === 'string' && ['sign_up', 'search', 'login', 'close_modal'].includes(action)) {
        switch (action) {
          case 'search':
          case 'login':
            handleOpenModal(action, { preventScrollReset: true });
            break;
          case 'sign_up':
            handleOpenModal('signup', { preventScrollReset: true });
            break;
          case 'close_modal':
            // Use modal context onClose if available and it's an email capture modal
            if (modalContext?.onClose) {
              modalContext.onClose();
            } else {
              handleCloseModal({ preventScrollReset: true });
              if (openPageModalType) {
                setCloseModalActionDispatched(true);
              }
            }
            break;
          default:
            navigate(url.pathname + url.search);
        }

        return;
      }
      navigate(url.pathname + url.search);
    };
    isClientSideNavigation = true;
  }

  // This is a special case for the product buy action
  // We intercept the button and render a different component to ensure the payment flow is handled correctly
  if (mergeTag === 'product.buy_action' && product) {
    const isProductArchived = product.status === 'archived';

    return (
      <BuyNowAction product={product}>
        {({ isLoading }) => (
          <ButtonElement
            type="submit"
            isClientSideNavigation={true}
            element={nodeWithModifiedAttrs}
            disabled={isLoading || isProductArchived}
          >
            {productButtonChildren}
          </ButtonElement>
        )}
      </BuyNowAction>
    );
  }

  return (
    <ButtonElement
      element={nodeWithModifiedAttrs}
      onClick={handleClientSideNavigation}
      isClientSideNavigation={isClientSideNavigation}
    >
      {children}
    </ButtonElement>
  );
};

export default Button;
